/**
 * AuthReadyGate - Singleton service that gates ALL authenticated API calls
 *
 * This solves the authentication initialization race condition by ensuring that
 * no API calls can proceed until authentication is fully initialized.
 *
 * Pattern: Singleton Promise Gate
 * - A promise is created when the module loads
 * - All axios requests await this promise before proceeding
 * - AuthenticationGuard marks auth as ready after tokens are set and validated
 * - Once resolved, subsequent awaits complete immediately (promise caching)
 *
 * This prevents:
 * - API calls before tokens are available (401 errors)
 * - Reload loops from axios interceptor catching 401s during initialization
 * - Race conditions between Auth0 SDK and API calls
 *
 * @see https://www.jonmellman.com/posts/singleton-promises/
 */

export type AuthPhase =
  | 'not_started' // Initial state before auth begins
  | 'initializing' // Auth0 SDK loading, tokens being fetched
  | 'health_check' // Validating tokens with backend
  | 'loading_data' // Fetching initial app data
  | 'ready' // Fully initialized, API calls can proceed
  | 'not_required' // Auth not required (public routes, unauthenticated users)
  | 'failed'; // Auth failed, redirect to login happening

class AuthReadyGateService {
  private static instance: AuthReadyGateService;

  private readyPromise: Promise<void>;
  private resolveReady!: () => void;
  private rejectReady!: (reason: Error) => void;

  private phase: AuthPhase = 'not_started';
  private isResolved = false;

  private constructor() {
    this.readyPromise = this.createNewPromise();
  }

  static getInstance(): AuthReadyGateService {
    if (!AuthReadyGateService.instance) {
      AuthReadyGateService.instance = new AuthReadyGateService();
    }
    return AuthReadyGateService.instance;
  }

  private createNewPromise(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
  }

  /**
   * Update the current auth phase for debugging/monitoring
   */
  setPhase(phase: AuthPhase): void {
    const previousPhase = this.phase;
    this.phase = phase;
    console.debug(`[AuthReadyGate] Phase: ${previousPhase} → ${phase}`);
  }

  /**
   * Get current auth phase
   */
  getPhase(): AuthPhase {
    return this.phase;
  }

  /**
   * Check if auth is in initialization phase (not yet ready)
   * Used by axios interceptor to know whether to handle 401s
   */
  isInitializing(): boolean {
    return (
      this.phase === 'not_started' ||
      this.phase === 'initializing' ||
      this.phase === 'health_check' ||
      this.phase === 'loading_data'
    );
  }

  /**
   * Mark auth as not required (for unauthenticated flows)
   * This releases any waiting requests but signals that auth wasn't performed
   */
  markNotRequired(): void {
    if (this.isResolved) {
      console.debug(
        '[AuthReadyGate] Already resolved, ignoring markNotRequired'
      );
      return;
    }

    console.debug('[AuthReadyGate] Marking auth as NOT_REQUIRED');
    this.phase = 'not_required';
    this.isResolved = true;
    this.resolveReady();
  }

  /**
   * Check if auth is fully ready
   */
  isReady(): boolean {
    return this.phase === 'ready' && this.isResolved;
  }

  /**
   * Mark authentication as ready - releases all waiting requests
   * Called by AuthenticationGuard after successful initialization
   */
  markReady(): void {
    if (this.isResolved) {
      console.debug('[AuthReadyGate] Already ready, ignoring duplicate call');
      return;
    }

    console.debug('[AuthReadyGate] Marking auth as READY');
    this.phase = 'ready';
    this.isResolved = true;
    this.resolveReady();
  }

  /**
   * Mark authentication as failed - rejects all waiting requests
   * Called when auth fails and redirect to login is happening
   */
  markFailed(reason: string): void {
    if (this.isResolved) {
      console.debug(
        '[AuthReadyGate] Already resolved, ignoring failure:',
        reason
      );
      return;
    }

    console.debug('[AuthReadyGate] Marking auth as FAILED:', reason);
    this.phase = 'failed';
    this.isResolved = true;
    this.rejectReady(new Error(`Authentication failed: ${reason}`));
  }

  /**
   * Wait until auth is ready - used by axios interceptor
   * Returns immediately if already ready (promise caching)
   *
   * @param timeoutMs - Optional timeout to prevent infinite waiting
   * @returns Promise that resolves when auth is ready
   * @throws Error if auth fails or times out
   */
  async waitUntilReady(timeoutMs?: number): Promise<void> {
    // If already ready, return immediately
    if (this.isReady()) {
      return Promise.resolve();
    }

    // If no timeout, just wait for the promise
    if (!timeoutMs) {
      return this.readyPromise;
    }

    // Race between ready promise and timeout
    return Promise.race([
      this.readyPromise,
      new Promise<void>((_, reject) => {
        setTimeout(() => {
          reject(
            new Error(
              `Auth initialization timed out after ${timeoutMs}ms (phase: ${this.phase})`
            )
          );
        }, timeoutMs);
      }),
    ]);
  }

  /**
   * Reset the gate for re-authentication (e.g., after logout)
   * Creates a new promise that will block requests until auth completes again
   */
  reset(): void {
    console.debug('[AuthReadyGate] Resetting for re-authentication');
    this.phase = 'not_started';
    this.isResolved = false;
    this.readyPromise = this.createNewPromise();
  }

  /**
   * Get debug info for monitoring
   */
  getDebugInfo(): { phase: AuthPhase; isResolved: boolean } {
    return {
      phase: this.phase,
      isResolved: this.isResolved,
    };
  }
}

// Export singleton instance
export const authReadyGate = AuthReadyGateService.getInstance();

// Export class for testing
export { AuthReadyGateService };
